// src/components/workspace/hooks/useAssignees.ts
import { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  FirestoreError
} from 'firebase/firestore';
import { Assignee } from '../types';
import { firestore } from '../../../firebase/config';

export const useAssignees = (workspaceId: string) => {
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');

  useEffect(() => {
    const assigneesRef = collection(firestore, `workspaces/${workspaceId}/assignees`);
    
    const unsubscribe = onSnapshot(
      assigneesRef,
      (snapshot) => {
        const assigneesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Assignee[];
        
        setAssignees(assigneesData);
        setStatus('success');
      },
      (error: FirestoreError) => {
        console.error('Assignees error:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  const addAssignee = async (data: Omit<Assignee, 'id'>) => {
    const assigneesRef = collection(firestore, `workspaces/${workspaceId}/assignees`);
    return addDoc(assigneesRef, data);
  };

  const updateAssignee = async (id: string, data: Partial<Omit<Assignee, 'id'>>) => {
    const assigneeRef = doc(firestore, `workspaces/${workspaceId}/assignees/${id}`);
    return updateDoc(assigneeRef, data);
  };

  const deleteAssignee = async (id: string) => {
    const assigneeRef = doc(firestore, `workspaces/${workspaceId}/assignees/${id}`);
    return deleteDoc(assigneeRef);
  };

  return {
    assignees,
    status,
    addAssignee,
    updateAssignee,
    deleteAssignee
  };
};